import React, { FC, useState, useEffect } from 'react';
import {
    Text, Flex, ActionButton, View
} from '@adobe/react-spectrum';
import Close from '@spectrum-icons/workflow/Close';

import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import { ContentAnimation } from '~/Scene/Content/contentAnimation';
import { InputSliderField } from '~/Components/Input/inputSliderField';
import { _environment } from '~/Services/Studio/studioScene';

export type GizmoMode = 'position' | 'rotation' | 'scale'

interface ICanvasGizmoProps {
    content: ContentAnimation;
    mode: GizmoMode
}

type IAxis = 'x' | 'y' | 'z';

export const CanvasGizmoGeometry: FC<ICanvasGizmoProps> = (
    { content, mode }: ICanvasGizmoProps
) => {
    const [step, setStep] = useState(1);
    const [min, setMin] = useState(1);
    const [max, setMax] = useState(1);
    const [visible, setVisible] = useState(false);
    const [values, setValues] = useState(Vector3.Zero());

    const setMinMaxStep = (min: number, max: number, step: number) => {
        setMin(min);
        setMax(max);
        setStep(step);
    };

    const setContentGeometry = (axe: IAxis, value: number, reset = false): void => {
        const newValues = values.clone();
        newValues[axe] = value;
        if (!reset) { setValues(newValues); }
        if (mode === 'position') content.setPosition(newValues);
        else if (mode === 'rotation') content.setRotation(newValues);
        else if (mode === 'scale') content.setScale(newValues);
        _environment.resetShadowGroundHeight();
    };

    const reset = (): void => {
        let vec: Vector3;
        if (mode === 'scale') {
            vec = Vector3.One();
            content.setScale(vec);
        } else if (mode === 'rotation') {
            vec = Vector3.Zero();
            content.setRotation(vec);
        } else if (mode === 'position') {
            vec = new Vector3(0, 0.5, 0);
            content.setPosition(vec);
        }
        setValues(vec);
    };

    const checkGeometry = (): void => {
        if (mode === 'position') {
            setMinMaxStep(-2, 2, 0.1);
            setValues(content.position);
        } else if (mode === 'rotation') {
            setMinMaxStep(-180, 180, 1);
            setValues(content.rotation);
        } else if (mode === 'scale') {
            setMinMaxStep(0.1, 10, 0.1);
            setValues(content.scale);
        }
    };

    useEffect(() => {
        checkGeometry();
        setVisible(true);
    }, [mode, content]);

    const capitalizeFirstLetter = (string: string): string => {
        const firsstLetter = string.charAt(0).toUpperCase();
        return firsstLetter + string.slice(1);
    };

    return (
        <View backgroundColor="gray-100" borderRadius="medium" width="100%">
            {visible
                && (
                    <Flex direction="column" alignItems="center">
                        <View marginTop="size-100" marginBottom="size-100" width="90%">
                            <Flex direction="row" alignItems="center">
                                <Text>{capitalizeFirstLetter(mode)}</Text>
                                <View flex />
                                <ActionButton isQuiet onPress={() => { setVisible(false); }}>
                                    <Close size="M" aria-label="Close" />
                                </ActionButton>
                            </Flex>
                        </View>
                        <InputSliderField
                            min={min}
                            max={max}
                            step={step}
                            label="X"
                            width="size-1700"
                            defaultValue={values.x}
                            onChange={(value) => { setContentGeometry('x', value); }}
                        />
                        {mode !== 'scale'
                            && (
                                <>
                                    <InputSliderField
                                        min={min}
                                        max={max}
                                        step={step}
                                        label="Y"
                                        width="size-1700"
                                        defaultValue={values.y}
                                        onChange={(value) => { setContentGeometry('y', value); }}
                                    />
                                    <InputSliderField
                                        min={min}
                                        max={max}
                                        step={step}
                                        label="Z"
                                        width="size-1700"
                                        defaultValue={values.z}
                                        onChange={(value) => { setContentGeometry('z', value); }}
                                    />
                                </>
                            )}
                        <ActionButton isQuiet onPress={() => { reset(); }}>
                            <Text>{`Reset ${mode}`}</Text>
                        </ActionButton>
                    </Flex>
                )}
        </View>
    );
};
